/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { useContext, useLayoutEffect } from 'react';

import { RouteSnapshotDispatchContext } from './route-snapshot-dispatch-context';
import {
  useCommandIntelligenceRouteMatches,
  useCommandIntelligenceRouteParams,
  useCommandIntelligenceRoutePathname,
} from './use-command-intelligence-route-matches';

const mergeRouteSnapshot = (prev, { matches, routeParams, pathname }) => {
  const prevSnapshot = prev ?? { matches: [], routeParams: {}, pathname: '' };
  const incomingPathname = typeof pathname === 'string' ? pathname : '';
  const prevPathname = prevSnapshot.pathname ?? '';
  const pathnameChanged = incomingPathname.length > 0 && prevPathname.length > 0 && incomingPathname !== prevPathname;

  let nextMatches = Array.isArray(matches) ? matches : [];

  if (nextMatches.length === 0) {
    nextMatches = pathnameChanged ? [] : prevSnapshot.matches ?? [];
  } else if (!pathnameChanged) {
    const prevMatches = prevSnapshot.matches ?? [];

    if (prevMatches.length > nextMatches.length) {
      nextMatches = prevMatches;
    }
  }

  const incomingParams = routeParams && typeof routeParams === 'object' ? routeParams : {};
  const hasIncomingParams = Object.keys(incomingParams).length > 0;
  const nextParams = hasIncomingParams ? incomingParams : pathnameChanged ? {} : prevSnapshot.routeParams ?? {};
  const nextPathname = incomingPathname || prevPathname;

  return {
    matches: nextMatches,
    routeParams: nextParams,
    pathname: nextPathname,
  };
};

export const useAmbientRouteContextSync = () => {
  const setRouteSnapshot = useContext(RouteSnapshotDispatchContext);
  const matches = useCommandIntelligenceRouteMatches();
  const params = useCommandIntelligenceRouteParams();
  const pathname = useCommandIntelligenceRoutePathname();

  useLayoutEffect(() => {
    setRouteSnapshot?.(prev =>
      mergeRouteSnapshot(prev, {
        matches,
        routeParams: params ?? {},
        pathname,
      })
    );
  }, [matches, params, pathname, setRouteSnapshot]);
};
